/**
 * Main layout component with AppBar, navigation, and user menu
 * Includes logout functionality and language switcher
 */
import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Container,
  Avatar,
  Tooltip,
  ListItemIcon,
  Divider,
  Button,
  Popper,
  Paper,
  MenuList,
  ClickAwayListener,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout as LogoutIcon,
  Language as LanguageIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/auth.store';
import { useTrackProjectNavigation } from '@features/projects/hooks/useTrackProjectNavigation';
import { useAuth } from '@features/auth/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@core/constants/routes.constants';

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const { logout } = useAuth();
  const { t, i18n } = useTranslation('common');

  // Track project page navigation for "resume where you left off"
  useTrackProjectNavigation();

  const [anchorElLang, setAnchorElLang] = useState<null | HTMLElement>(null);
  const [isDesktopSettingsOpen, setIsDesktopSettingsOpen] = useState(false);
  const [isMobileSettingsOpen, setIsMobileSettingsOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  
  const desktopSettingsButtonRef = useRef<HTMLButtonElement>(null);
  const mobileSettingsButtonRef = useRef<HTMLButtonElement>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setAnchorElLang(null);
    setIsDesktopSettingsOpen(false);
    setIsMobileSettingsOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  const formatRole = (role?: string | string[]): string => {
    if (!role) return '';
    const roleStr = Array.isArray(role) ? role[0] : role;
    if (!roleStr) return '';
    switch (roleStr) {
      case 'ADMIN':
        return 'Admin';
      case 'MEMBER':
        return 'Member';
      default:
        return roleStr;
    }
  };

  const handleToggleUserMenu = (): void => {
    setIsUserMenuOpen((prev) => !prev);
  };

  const handleCloseUserMenu = (): void => {
    setIsUserMenuOpen(false);
  };

  const handleToggleDesktopSettings = (): void => {
    setIsDesktopSettingsOpen((prev) => !prev);
  };

  const handleCloseDesktopSettings = (): void => {
    setIsDesktopSettingsOpen(false);
  };

  const handleToggleMobileSettings = (): void => {
    setIsMobileSettingsOpen((prev) => !prev);
  };

  const handleCloseMobileSettings = (): void => {
    setIsMobileSettingsOpen(false);
  };

  const handleCloseLangMenu = (): void => {
    setAnchorElLang(null);
  };

  const handleLanguageChange = (lang: string): void => {
    i18n.changeLanguage(lang);
    handleCloseLangMenu();
  };

  const handleNavigateFromDesktopSettings = (route: string): void => {
    setIsDesktopSettingsOpen(false);
    navigate(route);
  };

  const handleNavigateFromMobileSettings = (route: string): void => {
    setIsMobileSettingsOpen(false);
    navigate(route);
  };

  const handleNavigateToProfile = (): void => {
    setIsUserMenuOpen(false);
    navigate(ROUTES.PROFILE);
  };

  const handleLogout = async (): Promise<void> => {
    setIsUserMenuOpen(false);
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: '#284375',
          borderBottom: 'none',
          boxShadow: 'none',
          flexShrink: 0,
        }}
      >
        <Container maxWidth={false} sx={{ px: 1 }}>
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 64, sm: 72 },
              px: { xs: 1, sm: 1 },
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': { opacity: 0.9 },
              }}
              onClick={() => navigate(ROUTES.DASHBOARD)}
            >
              <Box
                component="img"
                sx={{
                  height: { xs: 32, sm: 40 },
                  width: 'auto',
                  mr: 1.5,
                }}
              />
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.4rem', sm: '1.75rem' },
                  display: { xs: 'none', sm: 'block' },
                  letterSpacing: '-0.5px',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                {t('app.title')}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {(user?.roles?.includes('ADMIN') || user?.role?.includes('ADMIN')) && (
                  <>
                    <Button
                      ref={desktopSettingsButtonRef}
                      onClick={handleToggleDesktopSettings}
                      endIcon={<ArrowDropDownIcon sx={{ fontSize: 20 }} />}
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '1.05rem',
                        textTransform: 'none',
                        px: 3,
                        py: 1,
                        borderRadius: '20px',
                        border: '2px solid transparent',
                        background: 'transparent',
                        '&:hover': {
                          border: '2px solid rgba(255, 255, 255, 0.6)',
                          background: 'rgba(255, 255, 255, 0.1)',
                        },
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                      }}
                    >
                      Settings
                    </Button>
                    <Popper
                      open={isDesktopSettingsOpen}
                      anchorEl={desktopSettingsButtonRef.current}
                      placement="bottom-end"
                      style={{ zIndex: 1300 }}
                    >
                      <ClickAwayListener onClickAway={handleCloseDesktopSettings}>
                        <Paper
                          elevation={8}
                          sx={{
                            mt: 1,
                            minWidth: 200,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                          }}
                        >
                          <MenuList>
                            <MenuItem onClick={() => handleNavigateFromDesktopSettings(ROUTES.CONFIGURATION_LIST)}>
                              Configuration List
                            </MenuItem>
                            <MenuItem onClick={() => handleNavigateFromDesktopSettings(ROUTES.USERS)}>
                              User Management
                            </MenuItem>
                          </MenuList>
                        </Paper>
                      </ClickAwayListener>
                    </Popper>
                  </>
                )}
              </Box>

              {(user?.roles?.includes('ADMIN') || user?.role?.includes('ADMIN')) && (
                <>
                  <IconButton
                    ref={mobileSettingsButtonRef}
                    sx={{
                      display: { xs: 'flex', md: 'none' },
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      cursor: 'pointer',
                    }}
                    onClick={handleToggleMobileSettings}
                  >
                    <MenuIcon />
                  </IconButton>
                  <Popper
                    open={isMobileSettingsOpen}
                    anchorEl={mobileSettingsButtonRef.current}
                    placement="bottom-end"
                    style={{ zIndex: 1300 }}
                  >
                    <ClickAwayListener onClickAway={handleCloseMobileSettings}>
                      <Paper
                        elevation={8}
                        sx={{
                          mt: 1,
                          minWidth: 200,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        }}
                      >
                        <MenuList>
                          <MenuItem onClick={() => handleNavigateFromMobileSettings(ROUTES.CONFIGURATION_LIST)}>
                            Configuration List
                          </MenuItem>
                          <MenuItem onClick={() => handleNavigateFromMobileSettings(ROUTES.USERS)}>
                            User Management
                          </MenuItem>
                        </MenuList>
                      </Paper>
                    </ClickAwayListener>
                  </Popper>
                </>
              )}

              <Tooltip title="Account">
                <IconButton
                  ref={userMenuButtonRef}
                  onClick={handleToggleUserMenu}
                  sx={{
                    p: 0,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: '#006695',
                      width: { xs: 38, sm: 44 },
                      height: { xs: 38, sm: 44 },
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      border: '2px solid rgba(255,255,255,0.3)',
                    }}
                  >
                    {user?.name?.charAt(0).toUpperCase() || 'A'}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Popper
                open={isUserMenuOpen}
                anchorEl={userMenuButtonRef.current}
                placement="bottom-end"
                style={{ zIndex: 1300 }}
              >
                <ClickAwayListener onClickAway={handleCloseUserMenu}>
                  <Paper
                    elevation={8}
                    sx={{
                      mt: 1,
                      minWidth: 220,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    }}
                  >
                    <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary">
                        {user?.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
                        {formatRole(user?.role)}
                      </Typography>
                    </Box>
                    <Divider />
                    <MenuList>
                      <MenuItem onClick={handleNavigateToProfile}>
                        <ListItemIcon>
                          <AccountCircle fontSize="small" />
                        </ListItemIcon>
                        {t('navigation.profile')}
                      </MenuItem>
                      <Divider />
                      <MenuItem onClick={handleLogout}>
                        <ListItemIcon>
                          <LogoutIcon fontSize="small" />
                        </ListItemIcon>
                        {t('navigation.logout')}
                      </MenuItem>
                    </MenuList>
                  </Paper>
                </ClickAwayListener>
              </Popper>

              <Menu
                sx={{ mt: '45px' }}
                id="language-menu"
                anchorEl={anchorElLang}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElLang)}
                onClose={handleCloseLangMenu}
                disablePortal
                disableAutoFocusItem
                disableEnforceFocus
                disableRestoreFocus
                slotProps={{ backdrop: { invisible: true } }}
              >
                <MenuItem
                  onClick={() => handleLanguageChange('en')}
                  selected={i18n.language === 'en'}
                >
                  <ListItemIcon>
                    <LanguageIcon fontSize="small" />
                  </ListItemIcon>
                  English
                </MenuItem>
                <MenuItem
                  onClick={() => handleLanguageChange('fr')}
                  selected={i18n.language === 'fr'}
                >
                  <ListItemIcon>
                    <LanguageIcon fontSize="small" />
                  </ListItemIcon>
                  Français
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1, bgcolor: '#F5F5F5', overflow: 'auto', position: 'relative' }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;
